<template>
  <div class="products-page">
    <!-- 复制自HomePage.vue的导航区域 -->
    <!-- 添加导航触发区域 -->
    <ProductNavigation />

    <!-- 产品页面头部section -->
    <section class="product-hero-section">
     
    </section>

    <!-- 产品列表section -->
    <section class="product-list-section">
      <!-- 筛选栏容器 -->
      <div class="filter-container">
        <div class="filter-item">
          <label class="filter-label">{{ t('products.productType') }}:</label>
          <div class="model-selector">
            <button 
              v-for="model in productModels" 
              :key="model"
              @click="selectModel(model)"
              :class="{ 'active': selectedModel === model }"
            >
              {{ getModelLabel(model) }}
            </button>
          </div>
        </div>
      </div>
      <!-- 动态产品展示网格 -->
      <div class="product-grid">
        <!-- 加载状态 -->
        <div v-if="loading" class="loading-spinner">{{ t('common.loading') }}</div>
          <!-- 产品项 -->
          <router-link 
            :to="{ name: 'ProductDetail', params: { id: product.id } }"
            class="product-link"
            v-for="product in filteredProducts" 
            :key="product.id"
          >
            <div class="product-item">
              <img :src="product.imageUrl || '@/assets/product1.png'" :alt="product.title">
              <p class="product-name">{{ product.title }}</p>
            </div>
          </router-link>
          <!-- 移除管理员操作按钮 -->
      </div>

        <!-- 无数据状态 -->
      <div v-if="!loading && filteredProducts.length === 0" class="no-data">
          {{ t('products.noProducts') }}
      </div>

            <!-- 分页控件 -->
      <div v-if="!loading && totalPages >= 1" class="pagination">
        <button class="page-btn" @click="changePage(currentPage - 1)" :disabled="currentPage === 1">{{ t('pagination.previous') }}</button>
        <button 
          class="page-btn"
          v-for="page in totalPages" 
          :key="page"
          @click="changePage(page)"
          :class="{ 'active': currentPage === page }"
        >
          {{ page }}
        </button>
        <button class="page-btn" @click="changePage(currentPage + 1)" :disabled="currentPage === totalPages">{{ t('pagination.next') }}</button>
      </div>

    </section>

    <!-- 页脚内容 -->
    <Footer />
  </div>
</template>

<script setup>
import { ref, onMounted, onUnmounted, computed, watch, onActivated } from 'vue';
import { useRoute } from 'vue-router';
import { getAllProducts } from '@/api/product';
import ProductNavigation from '@/components/ProductNavigation.vue';
import Footer from '@/components/Footer.vue';
import { useI18n } from 'vue-i18n';

// 初始化响应式变量
const { locale, t } = useI18n();
const route = useRoute();
const products = ref([]);
const selectedTag = ref('');
// 新增型号筛选相关变量
const productModels = ref([t('products.allTypes')]); // 初始值
const selectedModel = ref(t('products.allTypes'));
const currentPage = ref(1);
const pageSize = ref(6);
const loading = ref(false);

// 加载产品数据 - 修改为提取型号信息
const loadProducts = async () => {
  try {
    loading.value = true;
    const response = await getAllProducts(locale.value);
    products.value = response.data || [];
    
    // 提取所有类型并去重
    const models = [t('products.allTypes')]; // 默认选项
    const uniqueModels = new Set();
    products.value.forEach(product => {
      if (product.model) uniqueModels.add(product.model);
    });
    productModels.value = models.concat(Array.from(uniqueModels));
  } catch (error) {
    console.error('加载产品失败:', error);
    products.value = [];
    alert(t('products.loadFailed'));
  } finally {
    loading.value = false;
  }
};

// 筛选产品（仅筛选，不分页）
const allFilteredProducts = computed(() => {
  let result = products.value || [];
  // 标签筛选
  if (selectedTag.value) {
    result = result.filter(product => product.tag === selectedTag.value);
  }
  // 型号筛选
  if (selectedModel.value && selectedModel.value !== t('products.allTypes')) {
    result = result.filter(product => product.model === selectedModel.value);
  }
  return result;
});

// 分页处理（基于已筛选的全部产品）
const filteredProducts = computed(() => {
  const startIndex = (currentPage.value - 1) * pageSize.value;
  return allFilteredProducts.value.slice(startIndex, startIndex + pageSize.value);
});

// 新增型号选择方法
const selectModel = (model) => {
  selectedModel.value = model;
  currentPage.value = 1; // 重置页码
};

// 更新总页数计算逻辑
const totalPages = computed(() => {
  // 基于所有筛选后的产品总数计算总页数
  return Math.ceil(allFilteredProducts.value.length / pageSize.value) || 1;
});

// 切换页码
const changePage = (page) => {
  if (page >= 1 && page <= totalPages.value) {
    currentPage.value = page;
  }
};

// 产品分类翻译映射
const modelTranslations = {
  '智能通道系统': {
    'zh': '智能通道系统',
    'en': 'Intelligent Channel Systems'
  },
  '物联网中间件': {
    'zh': '物联网中间件',
    'en': 'IoT Middleware'
  },
  '传感器及组件': {
    'zh': '传感器及组件',
    'en': 'Sensors & Components'
  }
};

// 获取型号标签（用于显示，如果是"所有类型"则使用翻译）
const getModelLabel = (model) => {
  if (model === '所有类型' || model === t('products.allTypes')) {
    return t('products.allTypes');
  }
  // 如果model在翻译映射中，根据当前语言返回翻译
  if (modelTranslations[model]) {
    return modelTranslations[model][locale.value] || modelTranslations[model]['zh'];
  }
  // 如果model是英文翻译，需要找到对应的中文键
  const zhKey = Object.keys(modelTranslations).find(key => 
    modelTranslations[key][locale.value] === model
  );
  if (zhKey) {
    return modelTranslations[zhKey][locale.value] || model;
  }
  return model;
};

// 监听语言变化
watch(locale, () => {
  // 重置选择为"所有类型"的翻译
  selectedModel.value = t('products.allTypes');
  loadProducts();
});

// 监听导航栏类别变化事件
const handleCategoryChange = (event) => {
  const { model } = event.detail;
  
  if (!model) {
    return;
  }

  const tryUpdateModel = () => {
    if (productModels.value.length <= 1) {
      setTimeout(tryUpdateModel, 200);
      return;
    }

    if (productModels.value.includes(model)) {
      selectedModel.value = model;
      currentPage.value = 1;
    }
  };

  tryUpdateModel();
};

const setupEventListener = () => {
  window.addEventListener('category-changed', handleCategoryChange);
};

const removeEventListener = () => {
  window.removeEventListener('category-changed', handleCategoryChange);
};

onMounted(() => {
  loadProducts();
  setupEventListener();
});

onActivated(() => {
  setupEventListener();
});

watch(() => route.path, (newPath) => {
  if (newPath === '/products') {
    removeEventListener();
    setupEventListener();
  }
}, { immediate: true });

onUnmounted(() => {
  removeEventListener();
});
</script>

<style scoped lang="scss">
/* 从HomePage.vue复制的导航样式 */
@import '@/styles/nav_section';
/* 原有产品页面样式 */
@import '@/styles/sections/homepage/main_menu';
@import '@/styles/sections/product_page/product_head';
@import '@/styles/sections/product_page/product_list';
@import '@/styles/sections/homepage/footer_section';

</style>
